import { useLayoutEffect, useMemo, useRef } from "react";
import { getShaderColorFromString, ShaderFitOptions, defaultPatternSizing, DitheringShapes, DitheringTypes } from "@paper-design/shaders";
import { ShaderMount } from "@paper-design/shaders";
import { bannerFragmentShader } from "./ripplePalette";

type Props = { ink: string; lilac: string; background: string; active: boolean };

/** The approved ripple raster, with flowing violet and lilac accents in a single canvas. */
export default function BannerRipple({ ink, lilac, background, active }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const renderer = useRef<ShaderMount | null>(null);
  const uniforms = useMemo(() => ({
    u_colorBack: getShaderColorFromString(background),
    u_colorFront: getShaderColorFromString(ink),
    u_colorLilac: getShaderColorFromString(lilac),
    u_shape: DitheringShapes.ripple,
    u_type: DitheringTypes["4x4"],
    u_pxSize: 3,
    u_fit: ShaderFitOptions[defaultPatternSizing.fit],
    u_scale: 1.1,
    u_rotation: defaultPatternSizing.rotation,
    u_offsetX: defaultPatternSizing.offsetX,
    u_offsetY: defaultPatternSizing.offsetY,
    u_originX: defaultPatternSizing.originX,
    u_originY: defaultPatternSizing.originY,
    u_worldWidth: defaultPatternSizing.worldWidth,
    u_worldHeight: defaultPatternSizing.worldHeight,
  }), [ink, lilac, background]);
  useLayoutEffect(() => {
    if (!ref.current) return;
    try {
      const instance = new ShaderMount(ref.current, bannerFragmentShader, uniforms, undefined, 0, 0, 1, 700_000);
      renderer.current = instance;
      return () => { instance.dispose(); renderer.current = null; };
    } catch {
      // A disabled/unavailable GPU must not take the page content down with it.
      const canvas = ref.current.querySelector("canvas");
      canvas?.getContext("webgl2")?.getExtension("WEBGL_lose_context")?.loseContext();
      canvas?.remove();
    }
    // Mount once; palette and activity updates use the existing renderer below.
  }, []);
  useLayoutEffect(() => { renderer.current?.setUniforms(uniforms); }, [uniforms]);
  useLayoutEffect(() => { renderer.current?.setSpeed(active ? 0.14 : 0); }, [active]);
  return <div ref={ref} className="section-banner-renderer" />;
}
